import React from 'react'
import styled, { css } from 'styled-components'

import { type ClassNameProps, type StyleProps } from '@root/shared/react'

import { mediaMaxMobile } from '../theme'

import { DetectOverflow } from './detect-overflow'
import { hiddenScrollbarCss } from './hidden-scrollbar'

import type { DetectOverflowChildrenProps } from './detect-overflow'
import type { TypeHelper } from '@blade/std-lib'
import type { ReactElement } from 'react'

/**
 * CSS mask fade on overflowing edges.
 *
 * - `edge` (default) — fade only on edges where content is currently clipped
 * - `both` — fade on both edges of an axis whenever that axis overflows
 * - `always` — always show fades, no overflow detection
 *
 * See https://pqina.nl/blog/fade-out-overflow-using-css-mask-image/
 */

type Sizes = {
  inlineEnd: number
  inlineStart: number
  blockEnd: number
  blockStart: number
}

type FadeOverflowMode = 'always' | 'both' | 'edge'

type FadeOverflowProps = {
  variant?: 'vertical' | 'horizontal'
  /**
   * - `edge` — fade only where content is clipped (scroll-aware)
   * - `both` — fade on both edges if the axis overflows
   * - `always` — always show fades
   */
  mode?: FadeOverflowMode
  children:
    | React.ReactNode
    | ((
        props: TypeHelper.OmitKey<DetectOverflowChildrenProps, 'ref'>,
      ) => ReactElement)
  ref?: React.Ref<HTMLDivElement>
  asChild?: boolean
} & Partial<Sizes> &
  ClassNameProps &
  StyleProps &
  Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'ref'>

const EMPTY_OVERFLOW: TypeHelper.OmitKey<DetectOverflowChildrenProps, 'ref'> = {
  horizontalOverflow: false,
  verticalOverflow: false,
  overflowInlineStart: false,
  overflowInlineEnd: false,
  overflowBlockStart: false,
  overflowBlockEnd: false,
}

const maskSize = (active: boolean, size: number) =>
  active ? `${size}px` : '0px'

export const FadeOverflow = ({
  className,
  children,
  inlineEnd = 16,
  inlineStart = 16,
  blockEnd = 16,
  blockStart = 16,
  variant = 'horizontal',
  mode = 'edge',
  style,
  onScroll,
  ref,
  ...rest
}: FadeOverflowProps) => {
  const variables = {
    ...style,
    '--mask-inline-start': `${inlineStart}px`,
    '--mask-inline-end': `${inlineEnd}px`,
    '--mask-block-start': `${blockStart}px`,
    '--mask-block-end': `${blockEnd}px`,
    '--mask':
      variant === 'horizontal'
        ? 'var(--horizontal-mask)'
        : 'var(--vertical-mask)',
  } as React.CSSProperties

  if (mode === 'always') {
    const child =
      typeof children === 'function' ? children(EMPTY_OVERFLOW) : children
    return (
      <Root
        {...rest}
        data-horizontal-overflow
        data-vertical-overflow
        onScroll={onScroll}
        ref={ref}
        className={className}
        style={variables}
      >
        {child}
      </Root>
    )
  }

  return (
    <DetectOverflow>
      {({
        horizontalOverflow,
        verticalOverflow,
        overflowInlineStart,
        overflowInlineEnd,
        overflowBlockStart,
        overflowBlockEnd,
        ref: detectRef,
      }) => {
        const showInlineStart =
          mode === 'both' ? horizontalOverflow : overflowInlineStart
        const showInlineEnd =
          mode === 'both' ? horizontalOverflow : overflowInlineEnd
        const showBlockStart =
          mode === 'both' ? verticalOverflow : overflowBlockStart
        const showBlockEnd =
          mode === 'both' ? verticalOverflow : overflowBlockEnd

        const updatedVariables = {
          ...variables,
          // Always use px — bare `0` breaks `calc(100% - var(--mask-*-end))`
          // in some engines and drops the entire mask (both fades disappear).
          '--mask-inline-start': maskSize(showInlineStart, inlineStart),
          '--mask-inline-end': maskSize(showInlineEnd, inlineEnd),
          '--mask-block-start': maskSize(showBlockStart, blockStart),
          '--mask-block-end': maskSize(showBlockEnd, blockEnd),
        } as React.CSSProperties

        const child =
          typeof children === 'function'
            ? children({
                horizontalOverflow,
                verticalOverflow,
                overflowInlineStart,
                overflowInlineEnd,
                overflowBlockStart,
                overflowBlockEnd,
              })
            : children

        return (
          <Root
            {...rest}
            data-horizontal-overflow={horizontalOverflow}
            data-vertical-overflow={verticalOverflow}
            onScroll={onScroll}
            ref={(cmp) => {
              if (cmp) {
                detectRef.current = cmp
                if (ref && typeof ref === 'object') {
                  ref.current = cmp
                }
              }
            }}
            className={className}
            style={updatedVariables}
          >
            {child}
          </Root>
        )
      }}
    </DetectOverflow>
  )
}

const Root = styled.div`
  overflow: hidden;

  --horizontal-mask: linear-gradient(
    90deg,
    transparent,
    black var(--mask-inline-start),
    black calc(100% - var(--mask-inline-end)),
    transparent
  );
  --vertical-mask: linear-gradient(
    to bottom,
    transparent,
    black var(--mask-block-start),
    black calc(100% - var(--mask-block-end)),
    transparent
  );
  /* Apply the mask image and mask size variables */
  mask-image: var(--mask);
  mask-size: 100%;

  /* Position the content gradient in the top left, and the 
  scroll gradient in the top right */
  mask-position: 0 0;

  /* We don't repeat our mask images */
  mask-repeat: no-repeat, no-repeat;

  ${mediaMaxMobile(css`
    ${hiddenScrollbarCss}
  `)}
`
